// Route-level API contract for the corpus Edge Function.
// Request and response shapes for each endpoint — consumed by the platform fetch client
// and annotated on backend route handlers. No domain model definitions here; import
// those from ./models.ts if a response shape needs to embed a model type.

import { SCORECARD } from "@shared/scorecard.ts";
import type { ScorecardKey } from "@shared/scorecard.ts";
import type { DimensionReviewState, QueueItem, SourceStatus } from "./models.ts";
import type { AdmissionCode, RejectionFinding } from "@shared/types/rejections.ts";
import type { StoredJson } from "@shared/types/json.ts";
import type { Claim } from "@shared/types/claims.ts";
import type {
  TaxonomyGapContext,
  TaxonomyGapEntry,
  TaxonomyGapKind,
  TaxonomyGapStatus,
} from "@shared/types/taxonomy.ts";

// ── GET /corpus/documents ──────────────────────────────────────────────────────

export interface QueueListResponse {
  total: number;
  items: QueueItem[];
}

// ── POST /corpus/documents ─────────────────────────────────────────────────────

/** Document was successfully submitted — a Tangrowth extraction job was spawned. */
export interface CreateDocumentSpawnedResponse {
  kind: "spawned";
  url: string;
  /** sources.id — the stable handle for all subsequent /corpus/documents/:id calls. Not yet a
   *  real `sources` row — that's created once extraction completes (see processExtractionResult). */
  id: string;
  /** Pass to POST /corpus/documents/:id/extraction-status and poll until it resolves. */
  jobId: string;
}

/** The URL is already in the corpus (admitted + ingested). No extraction needed. */
export interface CreateDocumentDuplicateResponse {
  kind: "duplicate";
  url: string;
  id: string;
}

/** The URL was previously rejected at corpus admission. No extraction needed. */
export interface CreateDocumentRejectedResponse {
  kind: "rejected";
  url: string;
  rejection: RejectionFinding;
}

/** Discriminated response for POST /corpus/documents — short-circuits before Tangrowth
 *  for known-rejected and already-ingested URLs. */
export type CreateDocumentApiResponse =
  CreateDocumentSpawnedResponse | CreateDocumentDuplicateResponse | CreateDocumentRejectedResponse;

// ── POST /corpus/documents/:id/annotate ────────────────────────────────────────

export interface AnnotateResponse {
  /** "ingested" — the human-merged scorecard cleared every Tier-1 field; auto-ingested on save,
   *  no separate ingest step. "pending" — still escalated; see `reason`/`remainingFields`. */
  status: "ingested" | "pending";
  /** Why this decision was reached — see evaluateIngestDecision. */
  reason: string;
  /** current_score / potential_score of the human-merged scorecard, in [0, 1]. */
  rankStability: number;
  /** Tier-1 fields still blocking ingestion. Empty when status is "ingested". */
  remainingFields: ScorecardKey[];
}

// ── POST /corpus/documents/:id/ingest ──────────────────────────────────────────

export interface IngestResult {
  source_id: string;
  paragraph_count: number;
}

// ── GET /corpus/backlog/fields ────────────────────────────────────────────────

export interface BacklogFieldCount {
  field: ScorecardKey;
  count: number;
}

export interface BacklogFieldsResponse {
  fields: BacklogFieldCount[];
}

// ── GET /corpus/backlog/next ──────────────────────────────────────────────────

export interface BacklogWorkItem {
  sourceId: string;
  field: ScorecardKey;
}

export interface BacklogNextResponse {
  /** Null when the backlog (or the requested field's backlog) is empty. */
  item: BacklogWorkItem | null;
}

// ── GET /corpus/work-queue ────────────────────────────────────────────────────

/** A batch job that has items of one kind of work waiting on a person (open reviews, failures). */
export interface WorkQueueJob {
  jobId: string;
  name: string;
  kind: BatchJobKind;
  status: BatchJobStatus;
  /** Items owed: open reviews for a review group, failed items for a failure group. */
  itemCount: number;
}

/** The first jobs of a group, newest first, and how many jobs the group holds in all. */
export interface WorkQueueJobGroup {
  total: number;
  jobs: WorkQueueJob[];
}

/** One thing a person has to do next, each discriminated on `type`. */
export type WorkQueueAction =
  | { type: "blocked_documents"; count: number }
  | ({ type: "jobs_awaiting_review" } & WorkQueueJobGroup)
  | ({ type: "failed_jobs" } & WorkQueueJobGroup)
  | { type: "ready_documents"; count: number }
  | { type: "unhealthy_cron_jobs"; jobs: WorkQueueCronJob[] }
  | { type: "escalated_taxonomy_gaps"; count: number };

/** A scheduled job that keeps failing. */
export interface WorkQueueCronJob {
  jobName: string;
  consecutiveFailures: number;
}

/** What the caller has to do next, limited to what their roles can act on; empty when nothing is
 *  pending. */
export interface WorkQueueResponse {
  actions: WorkQueueAction[];
}

// ── GET /corpus/documents/missing-field-count ────────────────────────────────

/** How many pending, ready and ingested documents were scanned for one scorecard field, and how
 *  many have no labels for it (no human decision and no AI labels). `missing` is the number of
 *  documents a field_reannotation job over scope `all` with `annotationState: "missing"` would
 *  cover. */
export interface MissingFieldCountResponse {
  field: ScorecardKey;
  scanned: number;
  missing: number;
}

// ── POST /corpus/documents/:id/refresh ─────────────────────────────────────────

/** Result of refreshing a document's sections and embeddings. */
export interface RefreshDocumentContentResult {
  paragraph_count: number;
}

// ── POST /corpus/documents/:id/re-extract ──────────────────────────────────────

/** A human decision that a re-extraction left in place although the AI now says something else. */
export interface ReExtractContradiction {
  field: ScorecardKey;
  humanLabels: string[];
  humanState: DimensionReviewState;
  aiLabels: string[];
}

/** A targeted field whose AI labels a re-extraction changed. */
export interface ReExtractFieldChange {
  field: ScorecardKey;
  before: string[];
  after: string[];
  /** The AI's justification for the labels before, null when none was stored. */
  beforeJustification: string | null;
  /** The AI's justification for the labels after, null when the extraction gave none. */
  afterJustification: string | null;
}

export interface ReExtractResult {
  /** "unchanged" when every re-extracted field's AI labels matched what was already stored. */
  status: "updated" | "unchanged";
  /** Every targeted field whose AI labels changed, with the labels before and after. */
  changes: ReExtractFieldChange[];
  /** Human decisions the re-extraction did not change but the AI now disagrees with. */
  contradictions: ReExtractContradiction[];
}

// ── POST /corpus/documents/:id/extraction-status ────────────────────────────────

export interface ExtractionStatusResponse {
  /** "extracting" — still running, poll again with the same jobId.
   *  "ingested"/"pending" — extraction finished and was processed; terminal, stop polling.
   *  "failed" — the extraction itself errored or expired; see `reason`. Terminal — the caller
   *  must resubmit via POST /corpus/documents to retry, there is no retry-in-place.
   *  "rejected" — corpus admission gate hard-rejected the document; see `rejection`. Terminal —
   *  the URL is permanently rejected and will short-circuit on resubmission.
   *  "escalated" — admission gate ran but could not conclusively admit; document entered the
   *  annotation queue for human review; see `rejection` for the gate's diagnostic. Terminal. */
  status: "extracting" | "ingested" | "pending" | "failed" | "rejected" | "escalated";
  /** Why this decision (ingest/escalate) or failure was reached. Absent while "extracting". */
  reason?: string;
  /** current_score / potential_score at extraction time, in [0, 1]. Present only on
   *  ingested/pending. */
  rankStability?: number;
  /** Extracted title, once known. Present only on ingested/pending (null if Tangrowth found
   *  none) — absent while "extracting"/"failed"/"rejected"/"escalated". */
  title?: string | null;
  /** Admission gate finding — present when status is "rejected" or "escalated". */
  rejection?: RejectionFinding;
}

// ── POST /corpus/extraction-jobs/poll ───────────────────────────────────────────

/** One row in the in-progress panel — see POST /corpus/extraction-jobs/poll. */
export interface ExtractionJobListItem {
  sourceId: string;
  jobId: string;
  url: string;
  /** "pending" — still extracting, this poll call just tried to resolve it. "done"/"failed"
   *  are terminal, kept in the response for a short window after finalizing (see
   *  listRecentExtractionJobs) so the panel can show what just finished. */
  status: "pending" | "done" | "failed";
  /** Corpus admission gate verdict for "done" annotation jobs — see `outcome` on extraction_jobs.
   *  "admitted": document cleared the gate and entered the annotation queue.
   *  "escalated": gate ran but could not conclusively admit; curator must review before ingest.
   *  "rejected": gate hard-rejected the document; URL is permanently blocked.
   *  Null for still-pending/failed jobs, re-extraction jobs, and pre-migration rows. */
  outcome: "admitted" | "escalated" | "rejected" | null;
  /** Admission gate diagnostic for "rejected" and "escalated" outcomes; null otherwise.
   *  For "rejected": the rejection the gate returned (code + message).
   *  For "escalated": the diagnostic that triggered escalation (code + message). */
  rejection: RejectionFinding | null;
  /** Set once "done"; null while "pending"/"failed". */
  title: string | null;
  /** Set once "failed"; null otherwise. */
  error: string | null;
  createdAt: string;
}

export interface ExtractionJobPollResponse {
  jobs: ExtractionJobListItem[];
}

// ── GET /corpus/extraction-jobs/status ──────────────────────────────────────────

/** One (source type, URL pattern) entry the extraction backend can resolve a URL to — enough
 *  for the frontend to warn a curator before they submit an unsupported or best-effort URL.
 *  There is no catch-all/generic entry — an unrecognised URL matches nothing in this list at
 *  all, rather than falling into a low-accuracy fallback entry. Deliberately silent on which
 *  backend this describes (Tangrowth) — same as `warm` below, that's an implementation detail
 *  the frontend has no business knowing. */
export interface SupportedSourceConnector {
  name: string;
  description: string;
  urlPattern: string;
  /** "guaranteed" — every URL matching urlPattern will succeed. "best_effort" — a matching URL
   *  may still fail extraction outright (e.g. a PubMed URL with no PMC open-access copy). Plain
   *  string, not a union — Tangrowth's own vocabulary is authoritative, this is a mirror. */
  coverage: string;
}

export interface ExtractionJobsStatusResponse {
  /** True if the extraction backend responded within the warm-check timeout. False means cold
   *  (scaled to zero, still booting) — submitting right now risks the request outliving
   *  corpus's own CPU budget. Checking this also wakes it if it's cold. Deliberately silent on
   *  which backend this is (Tangrowth) — that's an implementation detail the frontend has no
   *  business knowing. */
  warm: boolean;
  /** Null whenever warm is false — no response means no manifest to report. */
  sourceConnectors: SupportedSourceConnector[] | null;
}

// ── GET /corpus/documents/:id/extraction-trace ──────────────────────────────────

/** One resolution step the connector took to produce the document — see
 *  document_extraction_traces.connector_attempts (supabase/DATABASE.md). Debug/observability
 *  only, secondary in Kodakku: something to check when surprised by an extraction result, not
 *  primary UI. */
export interface DocumentConnectorAttempt {
  step: string;
  outcome: string;
  detail: string | null;
}

/** How one retained section's title was routed to a section key — see
 *  document_extraction_traces.sections (supabase/DATABASE.md). */
export interface DocumentSectionClassification {
  key: string;
  rawTitle: string | null;
  method: string;
  confidence: number | null;
}

/** One axis answer from the admission gate's LLM assessment. */
export interface DocumentAdmissionAxisAnswer {
  value: string;
  justification: string;
}

/** The LLM's primary-finding assessment on three axes — present when the admission gate's LLM
 *  step ran and admitted the document. Absent on deterministic admits and escalations. */
export interface DocumentAdmissionFindingAssessment {
  primary_finding: string;
  data_origin: DocumentAdmissionAxisAnswer;
  health_target: DocumentAdmissionAxisAnswer;
  finding_type: DocumentAdmissionAxisAnswer;
}

/** The LLM's answer on what the paper reports its own results from — present when the
 *  compatibility step's LLM check ran and answered. */
export interface DocumentAdmissionCompatibilityAssessment {
  /** "single_study_type" | "animal_and_cell_only" | "multiple_study_types" */
  study_type_scope: DocumentAdmissionAxisAnswer;
}

/** What the corpus admission gate decided — the deciding step and any LLM assessments.
 *  Null when no trace has been recorded (pre-migration docs) or for escalated documents. */
export interface DocumentAdmissionTrace {
  /** "coverage.deterministic" | "compatibility.deterministic" | "compatibility.llm" |
   *  "evidence.deterministic" | "evidence.llm" */
  step: string;
  assessment: DocumentAdmissionFindingAssessment | null;
  /** Absent from traces recorded before the compatibility check existed. */
  compatibility?: DocumentAdmissionCompatibilityAssessment | null;
}

/** Response for GET /corpus/documents/:id/extraction-trace — null when no trace has ever been
 *  recorded for this document (predates migration 031, or extraction hasn't run yet). */
export type DocumentExtractionTraceResponse = {
  sourceProvider: string;
  connectorAttempts: DocumentConnectorAttempt[];
  sections: DocumentSectionClassification[];
  /** Null for documents extracted before this field was added (migration 20260926120000) or for
   *  admission-escalated documents where the gate could not reach a conclusive assessment. */
  admission: DocumentAdmissionTrace | null;
} | null;

// ── GET /corpus/documents/stats ─────────────────────────────────────────────────

export interface DocumentStats {
  pending: number;
  ready: number;
  ingested: number;
}

// ── GET /corpus/rejected ────────────────────────────────────────────────────────

/** One row from source_rejections — the gate's permanent record of a rejected URL. */
export interface RejectedItem {
  url: string;
  code: AdmissionCode;
  message: string | null;
  rejectedBy: string;
  rejectedAt: string;
}

export interface RejectedListResponse {
  items: RejectedItem[];
  total: number;
}

// ── POST /corpus/documents/:id/admission-check ──────────────────────────────────

/** Result of re-running the corpus admission gate against a document's current content. */
export interface AdmissionCheckResponse {
  outcome: "admitted" | "rejected" | "escalated";
  rejection: RejectionFinding | null;
  admission: DocumentAdmissionTrace;
}

// ── Batch jobs ────────────────────────────────────────────────────────────────

/** Every batch job kind. Each needs a definition registered in
 *  _shared/services/canonical/batch_jobs/internal/batch_job_registry.ts. */
export const BATCH_JOB_KINDS = [
  "admission_recheck",
  "pubmed_ingest",
  "field_reannotation",
  "document_indexing_refresh",
  "document_sections_refresh",
  "coverage_gap_recheck",
] as const;

export type BatchJobKind = (typeof BATCH_JOB_KINDS)[number];

export type BatchJobStatus = "queued" | "running" | "paused" | "completed" | "failed" | "cancelled";

export type BatchJobItemStatus = "pending" | "running" | "done" | "failed";

/** Review state of a batch job item that carries a human-actionable finding.
 *  Null (absent) means no review is needed for this item. */
export type BatchJobItemReview = "open" | "applied" | "dismissed";

/** API shape for a batch_jobs row. */
export interface BatchJob {
  id: string;
  kind: BatchJobKind;
  /** Label telling jobs of one kind apart; the creator's, or `{creator} - {kind} - {date}`. */
  name: string;
  params: Record<string, unknown>;
  status: BatchJobStatus;
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** Set once the job has been deleted (soft); null while it is live. */
  deletedAt: string | null;
  summary: Record<string, unknown> | null;
  /** Total item count, for a progress display — not stored on the job row itself. */
  itemCount: number;
  /** Items still pending or running, for a progress display. */
  openItemCount: number;
  /** Items that failed, so a curator can see at a glance which jobs need a retry. */
  failedItemCount: number;
  /** The corpus feed whose run this is; null for a job a person started. */
  corpusFeedId: string | null;
}

/** API shape for a batch_job_items row. `result` and `review` are left as unknown/nullable at
 *  this level — callers narrow by `kind` (e.g. AdmissionRecheckItemResult for admission_recheck). */
export interface BatchJobItem {
  id: string;
  batchJobId: string;
  subject: string;
  /** Display title for the subject source, hydrated at read time. */
  subjectTitle: string;
  /** Source URL for the subject, hydrated at read time. */
  subjectUrl: string;
  status: BatchJobItemStatus;
  attempts: number;
  result: Record<string, unknown> | null;
  error: string | null;
  review: BatchJobItemReview | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface BatchJobListResponse {
  items: BatchJob[];
  total: number;
}

/** POST /corpus/batch-jobs/:id/retry — how many failed items were put back in line. */
export interface RetryFailedBatchJobItemsResponse {
  retriedCount: number;
}

export interface BatchJobItemListResponse {
  items: BatchJobItem[];
  total: number;
}

// ── admission_recheck kind — POST /corpus/batch-jobs/admission-rechecks ─────────────

/** Every scope an admission_recheck batch job can cover. Never includes `revoked` sources —
 *  those have already left the corpus. */
export const ADMISSION_RECHECK_SCOPES = ["ingested", "pending_ready", "all"] as const;

/** Which documents an admission_recheck batch job covers. */
export type AdmissionRecheckScope = (typeof ADMISSION_RECHECK_SCOPES)[number];

/** Body of POST /corpus/batch-jobs/admission-rechecks, and the job's stored params. */
export interface AdmissionRecheckParams {
  scope: AdmissionRecheckScope;
  /** Names the job; left out or blank, it defaults to `{creator} - {kind} - {date}`. Kept in the
   *  job's `name` column, not its stored params. */
  name?: string;
}

/** processItem's result for one admission_recheck item, stored as batch_job_items.result. */
/** What re-running the admission gate found for one document; `skipped` when there was nothing
 *  to re-check (e.g. the source was deleted mid-batch). */
export type AdmissionRecheckOutcome = "admitted" | "rejected" | "escalated" | "skipped";

export interface AdmissionRecheckItemResult {
  outcome: AdmissionRecheckOutcome;
  rejection: RejectionFinding | null;
  admission: DocumentAdmissionTrace | null;
  /** Populated only when outcome is "skipped" (e.g. the source was deleted mid-batch). */
  reason?: string;
  /** Populated when this item's review was carried forward as "dismissed" from an earlier job's
   *  item that dismissed the same rejection code for the same subject. */
  carriedForwardFromItemId?: string;
}

/** summarize's result for an admission_recheck batch job, stored as batch_jobs.summary. */
export interface AdmissionRecheckSummary {
  byOutcome: Record<string, number>;
  byRejectionCode: Record<string, number>;
}

// ── pubmed_ingest kind — POST /corpus/batch-jobs/pubmed-ingests ─────────────────────

/** Most documents one pubmed_ingest job may ingest — each is a full extraction plus an LLM
 *  admission check, and the worker processes about five documents a minute, so a full job takes
 *  over three hours. */
export const PUBMED_INGEST_MAX_RESULTS = 1_000;

/** Body of POST /corpus/batch-jobs/pubmed-ingests, and the job's stored params. */
export interface PubmedIngestParams {
  /** A PubMed Central query in Entrez syntax, exactly as typed into the PMC search box. */
  query: string;
  /** Ingest at most this many of the query's matches. */
  maxResults: number;
  /** Names the job; left out or blank, it defaults to `{creator} - {kind} - {date}`. Kept in the
   *  job's `name` column, not its stored params. */
  name?: string;
}

/** Most matching articles a query preview describes, which is as many as a person reads. */
export const PUBMED_QUERY_PREVIEW_MAX_SAMPLE = 20;

/** One article a query matches, shown so a person can see whether the query finds the right
 *  papers. */
export interface PubmedQuerySampleArticle {
  title: string;
  /** Where the article is read, on PubMed Central. */
  url: string;
}

/** Response of GET /corpus/batch-jobs/pubmed-ingests/preview: what PMC says about a query. */
export interface PubmedQueryPreview {
  /** Total PMC articles the query matches, before any maxResults limit. */
  count: number;
  /** The query as NCBI expanded it — which fields and MeSH terms each phrase was mapped to. */
  queryTranslation: string;
  /** Everything NCBI flagged about the query short of rejecting it. NCBI is lenient: a malformed
   *  query usually runs anyway, so read these before trusting the count. */
  warnings: string[];
  /** The first matches, as many as the request asked for; empty when it asked for none. */
  sample: PubmedQuerySampleArticle[];
}

/** What became of one PMC article a pubmed_ingest job processed. An article the pipeline could
 *  not process at all is not an outcome: its item is `failed`, with the error on the item. */
export type PubmedIngestOutcome =
  /** Already in the corpus; nothing was done. */
  | "existing"
  /** Rejected by an earlier admission check or a person; nothing was done. */
  | "previously_rejected"
  /** Failed the corpus admission gate in this job and was rejected. */
  | "rejected"
  /** Passed the gate and was annotated and ingested without human review. */
  | "ingested"
  /** Admitted or escalated, but a person must annotate it before it can be ingested. */
  | "needs_review";

const PUBMED_INGEST_OUTCOME_LOOKUP: Record<PubmedIngestOutcome, true> = {
  existing: true,
  previously_rejected: true,
  rejected: true,
  ingested: true,
  needs_review: true,
};

/** Every pubmed_ingest outcome; the lookup above makes the compiler flag one added to the type
 *  and left out here. */
export const PUBMED_INGEST_OUTCOMES = Object.keys(
  PUBMED_INGEST_OUTCOME_LOOKUP
) as PubmedIngestOutcome[];

/** Why a pubmed_ingest item needs a person's review. */
export type PubmedIngestReviewReason =
  /** The admission gate could not decide whether the paper belongs in the corpus. */
  | "admission_unclear"
  /** Admitted, but an extracted field was not confident enough to ingest automatically. */
  | "low_confidence";

/** processItem's result for one pubmed_ingest item, stored as batch_job_items.result. */
export interface PubmedIngestItemResult {
  outcome: PubmedIngestOutcome;
  /** The corpus source, once one exists: every outcome except rejected and previously_rejected,
   *  and `existing` when the URL is only being extracted by another job. */
  sourceId: string | null;
  /** The paper's title, when extraction got far enough to read it. */
  title: string | null;
  /** The finding that rejected the paper: set for rejected and previously_rejected. */
  rejection: RejectionFinding | null;
  /** Set when outcome is needs_review. */
  reviewReason?: PubmedIngestReviewReason;
  /** Scorecard fields that stopped automatic ingestion. Set when reviewReason is low_confidence. */
  blockingFields?: string[];
}

/** summarize's result for a pubmed_ingest batch job, stored as batch_jobs.summary. */
export interface PubmedIngestSummary {
  byOutcome: Record<string, number>;
  byRejectionCode: Record<string, number>;
}

// ── field_reannotation kind — POST /corpus/batch-jobs/field-reannotations ────────────

/** Every scorecard field a field_reannotation job can re-annotate — all of them, in SCORECARD order. */
export const FIELD_REANNOTATION_FIELDS = Object.keys(SCORECARD) as [
  ScorecardKey,
  ...ScorecardKey[],
];

export type FieldReannotationField = ScorecardKey;

/** Which documents a field_reannotation job covers — the same scopes as admission_recheck. */
export const FIELD_REANNOTATION_SCOPES = ADMISSION_RECHECK_SCOPES;

export type FieldReannotationScope = AdmissionRecheckScope;

/** The annotation state of a document's field, which a field_reannotation job can restrict itself
 *  to: `human` — a person approved, overrode or set it; `non_human` — nothing a person decided
 *  (`ai` or `missing`); `ai` — only the AI suggested labels (in review or auto-approved);
 *  `missing` — no labels at all; `any` — no restriction. */
export const FIELD_ANNOTATION_STATES = ["any", "human", "non_human", "ai", "missing"] as const;

export type FieldAnnotationState = (typeof FIELD_ANNOTATION_STATES)[number];

/** Body of POST /corpus/batch-jobs/field-reannotations, and the job's stored params. */
export interface FieldReannotationParams {
  scope: FieldReannotationScope;
  /** The one scorecard field to re-annotate on every document in scope. */
  field: FieldReannotationField;
  /** Only documents whose `field` is in this state are re-annotated. Jobs stored before this param
   *  existed read as `any`. */
  annotationState: FieldAnnotationState;
  /** Names the job; left out or blank, it defaults to `{creator} - {kind} - {date}`. Kept in the
   *  job's `name` column, not its stored params. */
  name?: string;
}

/** What re-annotating the field did to one document. */
export type FieldReannotationOutcome =
  /** The AI labels were replaced by different ones; no human decision stands against them. */
  | "updated"
  /** The fresh AI labels match the stored ones (or the AI had already disagreed with the human). */
  | "unchanged"
  /** The AI labels changed and now disagree with a human decision, which was left in place. */
  | "contradicts_human"
  /** There was nothing to re-annotate (e.g. the source was deleted mid-batch). */
  | "skipped";

/** The human decision a contradiction stands against. */
export interface FieldReannotationHumanDecision {
  labels: string[];
  state: DimensionReviewState;
}

/** processItem's result for one field_reannotation item, stored as batch_job_items.result. */
export interface FieldReannotationItemResult {
  outcome: FieldReannotationOutcome;
  /** The re-annotated field, kept on the item so a later review does not depend on job params. */
  field: FieldReannotationField;
  /** The AI labels and their justifications before and after; null when outcome is skipped. Items
   *  recorded before justifications were kept carry none. */
  change: {
    before: string[];
    after: string[];
    beforeJustification?: string | null;
    afterJustification?: string | null;
  } | null;
  /** Set when outcome is contradicts_human. */
  human?: FieldReannotationHumanDecision;
  /** Set when outcome is skipped. */
  reason?: string;
  /** Set when this item's review was carried forward as "dismissed" from an earlier job's item
   *  that dismissed the same contradiction. */
  carriedForwardFromItemId?: string;
}

/** summarize's result for a field_reannotation batch job, stored as batch_jobs.summary. */
export interface FieldReannotationSummary {
  byOutcome: Record<string, number>;
}

// ── document_indexing_refresh kind — POST /corpus/batch-jobs/document-indexing-refreshes ──────

/** How much of the corpus a document_indexing_refresh job covers: `full` — every document still in the
 *  corpus (not revoked); `untagged` — only the documents with no field tags yet. */
export const DOCUMENT_INDEXING_REFRESH_MODES = ["full", "untagged"] as const;

export type DocumentIndexingRefreshMode = (typeof DOCUMENT_INDEXING_REFRESH_MODES)[number];

/** Body of POST /corpus/batch-jobs/document-indexing-refreshes, and the job's stored params. */
export interface DocumentIndexingRefreshParams {
  mode: DocumentIndexingRefreshMode;
  /** Names the job; left out or blank, it defaults to `{creator} - {kind} - {date}`. Kept in the
   *  job's `name` column, not its stored params. */
  name?: string;
}

/** What a document indexing refresh did to one document: `fields_added` when it gained at least one
 *  field tag, `no_new_fields` otherwise (including when only subjects were gained). */
export type DocumentIndexingRefreshOutcome = "fields_added" | "no_new_fields";

/** processItem's result for one document_indexing_refresh item, stored as batch_job_items.result. */
export interface DocumentIndexingRefreshItemResult {
  outcome: DocumentIndexingRefreshOutcome;
  /** Field URNs the document gained. */
  fieldsAdded: string[];
  /** How many subjects the document gained. */
  subjectsAdded: number;
  /** The document's field URNs after the refresh. */
  fieldsAfter: string[];
  /** How many main subjects the document has after the refresh. */
  mainSubjectCount: number;
}

/** summarize's result for a document_indexing_refresh batch job, stored as batch_jobs.summary. */
export interface DocumentIndexingRefreshSummary {
  byOutcome: Record<string, number>;
  /** Field URN to the number of documents that gained it. */
  fieldsGained: Record<string, number>;
  /** Documents with no field tags after the run. */
  withoutFields: number;
  /** Subjects gained across every document. */
  subjectsAdded: number;
  /** Documents with no main subject after the run. */
  withoutMainSubjects: number;
}

// ── document_sections_refresh kind — POST /corpus/batch-jobs/document-sections-refreshes ──────────────

/** Body of POST /corpus/batch-jobs/document-sections-refreshes. The job covers the documents whose section
 *  files are missing from Storage when it is created, so it has no stored params. */
export interface DocumentSectionsRefreshParams {
  /** Names the job; left out or blank, it defaults to `{creator} - {kind} - {date}`. Kept in the
   *  job's `name` column, not its stored params. */
  name?: string;
}

/** What a document sections refresh did to one document: `refreshed` when its sections were fetched again and
 *  re-embedded, `skipped` when there was nothing to refresh (the source is gone or revoked). */
export type DocumentSectionsRefreshOutcome = "refreshed" | "skipped";

/** processItem's result for one document_sections_refresh item, stored as batch_job_items.result. */
export interface DocumentSectionsRefreshItemResult {
  outcome: DocumentSectionsRefreshOutcome;
  /** Paragraphs embedded; null when the document was skipped. */
  paragraphCount: number | null;
  /** Set when outcome is skipped. */
  reason?: string;
}

/** summarize's result for a document_sections_refresh batch job, stored as batch_jobs.summary. */
export interface DocumentSectionsRefreshSummary {
  byOutcome: Record<string, number>;
  /** Paragraphs embedded across every refreshed document. */
  paragraphsEmbedded: number;
}

// ── GET /corpus/documents/missing-section-files ───────────────────────────────

/** How many pending, ready and ingested documents were checked for their section files in Storage,
 *  and how many lack the judge file, the excluded file, or either. `missingAny` is the number of
 *  documents a document_sections_refresh job would cover. */
export interface MissingSectionFilesResponse {
  checked: number;
  missingJudge: number;
  missingExcluded: number;
  missingAny: number;
}

// ── Corpus environment sync (design 040) ────────────────────────────────────────
// Served by the corpus-internal function. These are the contract between two environments running
// the same code version, written independently of how the database stores the data: the db layer
// maps between them and its tables in both directions.

/** One entry of GET /corpus-internal/documents/manifest — enough to match documents by `url`. */
export interface DocumentManifestEntry {
  id: string;
  url: string;
  status: SourceStatus;
}

export interface DocumentManifestResponse {
  items: DocumentManifestEntry[];
  total: number;
}

/** A document's identity and descriptive fields. Timestamps are ISO strings. */
export interface DocumentBundleSource {
  url: string;
  title: string;
  authors: string | null;
  sourceType: string | null;
  publishedDate: string | null;
  doi: string | null;
  scorecard: StoredJson;
  metadata: StoredJson;
  status: SourceStatus;
  ingestedAt: string | null;
  revokedAt: string | null;
  createdAt: string | null;
  fields: string[];
  subjects: string[];
  /** The subjects the document is mainly about, a subset of `subjects`. */
  mainSubjects: string[];
}

/** A document's annotation-pipeline state. */
export interface DocumentBundleWorkflow {
  aiScorecard: StoredJson;
  annotation: StoredJson;
  confidenceScores: StoredJson;
  rankStability: number | null;
  blockingFields: string[] | null;
  consensusScore: number | null;
  submittedAt: string | null;
  annotatedAt: string | null;
}

/** How a document's content was resolved and admitted. */
export interface DocumentBundleTrace {
  sourceProvider: string;
  connectorAttempts: StoredJson;
  sections: StoredJson;
  admission: StoredJson;
  createdAt: string;
}

/** One embedded paragraph, addressed by its section coordinates. */
export interface DocumentBundleEmbedding {
  sectionKey: string;
  sectionOrdinal: number;
  paragraphOrdinal: number;
  embedding: number[];
  createdAt: string | null;
}

/**
 * Everything one document is made of, portable between environments: GET/PUT
 * /corpus-internal/documents/:id/bundle. `workflow` and `trace` are null when the document has
 * none; `objects` maps each stored file of the document to its text by file name (e.g.
 * `judge_sections.json`); `embeddings` is empty for a revoked document.
 */
export interface DocumentBundle {
  bundleVersion: number;
  source: DocumentBundleSource;
  workflow: DocumentBundleWorkflow | null;
  trace: DocumentBundleTrace | null;
  objects: Record<string, string>;
  embeddings: DocumentBundleEmbedding[];
}

/** A rejection in full, including its triage details. The URL is its identity across environments. */
export interface SourceRejectionRecord {
  url: string;
  code: string;
  message: string | null;
  details: StoredJson;
  rejectedBy: string;
  rejectedAt: string;
}

export interface SourceRejectionRecordListResponse {
  items: SourceRejectionRecord[];
  total: number;
}

/** What importing a rejection did to the destination's document at that URL. */
export type SourceRejectionImportOutcome =
  "no_document" | "revoked" | "already_revoked" | "deleted";

/** Response of PUT /corpus-internal/rejections; `sourceId` is null for `no_document`. */
export interface SourceRejectionImportResult {
  outcome: SourceRejectionImportOutcome;
  sourceId: string | null;
}

// ── corpus feeds — /corpus/feeds ─────────────────────────────────────────────────────

/** Most articles a corpus feed's query may match when the feed is created. A feed takes at most
 *  PUBMED_INGEST_MAX_RESULTS new papers per run, so a query at this limit catches up in two. */
export const CORPUS_FEED_MAX_QUERY_MATCHES = 2_000;

/** The longest name a corpus feed may have, in characters. */
export const CORPUS_FEED_NAME_MAX_LENGTH = 120;

/** The longest description a corpus feed may have, in characters. */
export const CORPUS_FEED_DESCRIPTION_MAX_LENGTH = 2000;

/** Where a corpus feed is in its life: runs on schedule, kept but not run, or replaced for good. */
export const CORPUS_FEED_STATES = ["active", "paused", "retired"] as const;
export type CorpusFeedState = (typeof CORPUS_FEED_STATES)[number];

/** A named, standing PubMed query that brings papers into the corpus run by run. */
export interface CorpusFeed {
  id: string;
  name: string;
  description: string;
  query: string;
  /** Most new papers one run may bring in. */
  runCap: number;
  state: CorpusFeedState;
  createdBy: string;
  createdAt: string;
  /** When a person last edited the name, description or state; scheduling writes don't move it. */
  updatedAt: string;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

/** Body of POST /corpus/feeds. The query and the cap cannot be changed once the feed exists. */
export interface CreateCorpusFeedParams {
  name: string;
  description?: string;
  query: string;
  /** Defaults to PUBMED_INGEST_MAX_RESULTS, which is also the most it may be. */
  runCap?: number;
}

/** Body of PATCH /corpus/feeds/:id. Deliberately has no query or cap. */
export interface UpdateCorpusFeedParams {
  name?: string;
  description?: string;
  state?: CorpusFeedState;
}

/** What a corpus feed has brought in, over every run so far. A paper counts once however many
 *  runs there have been. */
export interface CorpusFeedStatistics {
  /** Every paper the feed has returned, whatever became of it. */
  returnedCount: number;
  /** The papers processed so far, by what became of them. Every outcome is present. */
  byOutcome: Record<PubmedIngestOutcome, number>;
  /** Papers still queued or being processed. */
  openCount: number;
  /** Papers that failed on every attempt, so have no outcome. */
  failedCount: number;
  /** Papers the admission gate rejected, now or earlier, over the papers processed; null until
   *  one has been. The quickest sign that a query is too loose. */
  rejectionRate: number | null;
  /** How far the feed's open run has got; null when no run is open. */
  openRun: CorpusFeedOpenRunProgress | null;
}

/** What a corpus feed's papers are made of; response of GET /corpus/feeds/:id/composition. Counts
 *  the papers that have a source in the corpus: ingested, already there, or waiting for review.
 *  Rejected papers have none, and a paper revoked since is not counted. */
export interface CorpusFeedComposition {
  /** Papers by health field URN. A paper counts under each field it is tagged with. */
  byField: Record<string, number>;
  /** Papers by MeSH subject (`mesh:<descriptor UI>`), a paper under each subject it carries. Only
   *  the CORPUS_FEED_COMPOSITION_MAX_SUBJECTS subjects with the most papers are here, and they are
   *  ids: a subject's name is looked up from /corpus/subjects/names. */
  bySubject: Record<string, number>;
}

/** The most subjects a corpus feed's composition lists: those with the most papers. */
export const CORPUS_FEED_COMPOSITION_MAX_SUBJECTS = 20;

/** The most subjects GET /corpus/subjects/names answers for in one request: a feed's composition
 *  lists at most CORPUS_FEED_COMPOSITION_MAX_SUBJECTS, so a page that names them asks once. */
export const SUBJECT_NAMES_MAX_IDS = 200;

/** Response of GET /corpus/subjects/names: the readable name of each subject asked for, by
 *  subject (`mesh:<descriptor UI>`). A subject with no known name is left out; names are display
 *  only, so a caller shows such a subject by its id. */
export type SubjectNamesBySubject = Record<string, string>;

/** What the corpus holds by health field; response of GET /corpus/catalogue/fields. Counts the
 *  papers ingested into the corpus. */
export interface CorpusCatalogueFields {
  /** Papers by health field URN. A paper counts under each field it is tagged with. */
  byField: Record<string, number>;
}

// ── Coverage gaps ──────────────────────────────────────────────────────────────

/** The longest name of a coverage gap. */
export const COVERAGE_GAP_NAME_MAX_LENGTH = 120;

/** The longest note on a coverage gap. */
export const COVERAGE_GAP_NOTE_MAX_LENGTH = 2000;

/** What coverage gaps can be ranked by first: the distinct people who asked, the claims in the gap
 *  still uncovered, or the most recent ask. The other figures break its ties. */
export const COVERAGE_GAP_SORTS = ["askers", "claims", "last_asked"] as const;

export type CoverageGapSort = (typeof COVERAGE_GAP_SORTS)[number];

/** Whether a coverage gap is still worked on. */
export const COVERAGE_GAP_STATES = ["open", "closed"] as const;

export type CoverageGapState = (typeof COVERAGE_GAP_STATES)[number];

/** Why a coverage gap was closed: the corpus now answers it, there is no research to answer it
 *  with, or it is not worth pursuing. */
export const COVERAGE_GAP_CLOSING_REASONS = ["filled", "no_research", "dismissed"] as const;

export type CoverageGapClosingReason = (typeof COVERAGE_GAP_CLOSING_REASONS)[number];

/** Body of POST /corpus/coverage-gaps/:id/closure. */
export interface CloseCoverageGapBody {
  reason: CoverageGapClosingReason;
  /** Why, in the operator's words; kept apart from the gap's own note. */
  note?: string;
}

/** Body of POST /corpus/coverage-gaps. */
export interface CreateCoverageGapBody {
  name: string;
  note?: string;
}

/** Body of PATCH /corpus/coverage-gaps/:id. */
export interface UpdateCoverageGapBody {
  name?: string;
  note?: string;
}

/** How much people want a coverage gap answered, over its claims still uncovered. */
export interface CoverageGapDemand {
  /** Claims of the gap that are still uncovered. */
  claims: number;
  /** Distinct people who asked across them: one who asked two of its claims counts once. */
  askers: number;
  /** Times its claims were asked and came back not found. */
  timesAsked: number;
  /** The most recent ask; null when none of its claims is uncovered any more. */
  lastAskedAt: string | null;
}

/** A person's decision to stop working on a coverage gap. It belongs to the gap, so describing its
 *  claims again neither lifts it nor loses it. */
export interface CoverageGapClosure {
  reason: CoverageGapClosingReason;
  note: string;
  closedAt: string;
  /** The `coverage_gap_recheck` job that checked the claims again; set only for a gap closed as
   *  filled. */
  recheckJobId: string | null;
  /** That job's status, so a caller can tell whether the claims' answers are in yet. */
  recheckJobStatus: BatchJobStatus | null;
}

/** What the corpus lacks that people want answered, grouped by hand from claims it could not
 *  answer; response of GET /corpus/coverage-gaps. */
export interface CoverageGap {
  id: string;
  name: string;
  note: string;
  createdAt: string;
  updatedAt: string;
  demand: CoverageGapDemand;
  /** Null while the gap is open. */
  closure: CoverageGapClosure | null;
  /** People who asked about its claims, now with an outcome to be told, and not yet told. */
  toTell: number;
}

/** What a person who asked about a claim has to be told: the corpus now answers it, or no research
 *  exists to answer it. */
export type CoverageGapRequesterOutcome = "answered" | "no_research";

/** A person who asked to be told about a claim of a closed coverage gap. Holds an email, so only
 *  the roles that run the catalogue read it; response of GET /corpus/coverage-gaps/:id/requesters. */
export interface CoverageGapRequester {
  /** The request, which is marked told. */
  id: string;
  email: string;
  claim: Pick<Claim, "id" | "canonical">;
  outcome: CoverageGapRequesterOutcome;
  /** The claim's verdict now; null when no research exists. */
  verdict: string | null;
  askedAt: string;
  /** When the person was marked told; null while they are still to be told. */
  toldAt: string | null;
}

// ── coverage_gap_recheck kind — started by closing a coverage gap as filled ───────────────────────

/** Params of a coverage_gap_recheck job: the gap whose claims are checked again. */
export interface CoverageGapRecheckParams {
  gapId: string;
}

/** Whether a claim of the gap gets an answer now: `answered` when its new verdict is anything but
 *  INSUFFICIENT, `unanswered` when the corpus still cannot answer it. */
export type CoverageGapRecheckOutcome = "answered" | "unanswered";

/** processItem's result for one coverage_gap_recheck item, stored as batch_job_items.result. */
export interface CoverageGapRecheckItemResult {
  outcome: CoverageGapRecheckOutcome;
  /** The claim's text, so the item can be shown by it. */
  canonical: string;
  /** The verdict the claim got on this check. */
  verdict: string;
}

/** summarize's result for a coverage_gap_recheck batch job, stored as batch_jobs.summary. */
export interface CoverageGapRecheckSummary {
  byOutcome: Record<string, number>;
}

/** How often a claim was asked and came back not found, and by how many people. No one is named. */
/** How the uncovered claims that are in no coverage gap are ranked. */
export const UNCOVERED_CLAIM_SORTS = ["last_asked", "askers", "asked"] as const;

export type UncoveredClaimSort = (typeof UNCOVERED_CLAIM_SORTS)[number];

export interface UncoveredClaimDemand {
  timesAsked: number;
  /** Distinct people who asked. */
  askers: number;
  firstAskedAt: string | null;
  lastAskedAt: string | null;
}

/** A claim we check that people asked and the corpus could not answer. */
export interface UncoveredClaim {
  claim: Claim;
  demand: UncoveredClaimDemand;
}

/** A claim in a coverage gap. The corpus may have answered it since: it stays in the gap, no longer
 *  uncovered and with no demand. */
export interface CoverageGapMember extends UncoveredClaim {
  uncovered: boolean;
}

/** One main subject of the corpus catalogue. */
export interface CorpusCatalogueSubject {
  /** The subject, `mesh:<descriptor UI>`. */
  subject: string;
  /** The subject's readable name; null when none is known, and a caller shows the id. */
  name: string | null;
  /** Ingested papers that have the subject among their main subjects. */
  papers: number;
}

/** One page of the corpus' main subjects, the one with the most papers first; response of
 *  GET /corpus/catalogue/subjects. */
export interface CorpusCatalogueSubjectPage {
  /** Subjects across every page. */
  total: number;
  items: CorpusCatalogueSubject[];
}

/** How far a corpus feed's open run has got through its papers. */
export interface CorpusFeedOpenRunProgress {
  /** Papers finished, whether they were processed or failed. */
  processedCount: number;
  /** Every paper in the run. */
  totalCount: number;
}

/** The most feeds GET /corpus/feeds/statistics answers for in one request: the largest page the
 *  feed list returns. */
export const CORPUS_FEED_STATISTICS_MAX_IDS = 200;

/** Response of GET /corpus/feeds/statistics: the statistics of each feed asked for, by feed id. A
 *  feed that does not exist is left out. */
export type CorpusFeedStatisticsByFeed = Record<string, CorpusFeedStatistics>;

/** What starting a corpus feed run did: began a job, found nothing new, or found one still open. */
export type CorpusFeedRunOutcome = "started" | "nothing_new" | "already_running";

/** Response of POST /corpus/feeds/:id/runs. */
export interface CorpusFeedRunResult {
  outcome: CorpusFeedRunOutcome;
  /** The feed as it stands after the run, with its last run stamped unless one was still open. */
  feed: CorpusFeed;
  /** The job that is this run; null unless the outcome is `started`. */
  job: BatchJob | null;
  /** Every article the query matches in PMC, whether or not the feed has returned it before; null
   *  when the run did not look because one was still open. */
  totalMatchCount: number | null;
  /** Of those, the articles the feed has never returned, before the cap is applied; null when the
   *  run did not look because one was still open. */
  newCount: number | null;
  /** Of the new articles, how many this run left for the next one because the cap was reached. */
  deferredCount: number;
}

// ── Taxonomy gaps — /corpus/taxonomy-gaps ───────────────────────────────────────────

/** A document that hit a gap. */
export interface TaxonomyGapDocumentRef {
  sourceId: string;
  url: string;
}

/** A vocabulary entry some document lacked, and where it stands. */
export interface TaxonomyGapReview {
  id: string;
  kind: TaxonomyGapKind;
  value: string;
  status: Exclude<TaxonomyGapStatus, "filled">;
  context: TaxonomyGapContext | null;
  /** The entry that will be inserted, set while the gap is decided. */
  decision: TaxonomyGapEntry | null;
  /** Why the judge could not settle it, set while the gap is escalated. */
  escalationReason: string | null;
  firstSeenAt: string;
  documentsTotal: number;
  /** Up to five of the documents that hit the gap. */
  documents: TaxonomyGapDocumentRef[];
}

/** Body of POST /corpus/taxonomy-gaps/:id/decision. */
export interface DecideTaxonomyGapRequest {
  entry: TaxonomyGapEntry;
}

/** A gap after a decision or a reopening. */
export interface TaxonomyGapStatusResponse {
  id: string;
  status: Extract<TaxonomyGapStatus, "open" | "decided">;
}
